import { useState } from 'react'
import type { PresupuestoObra } from './tipos'
import styles from './ModalVersionesPresupuesto.module.css'

interface ModalVersionesPresupuestoProps {
  abierto: boolean
  presupuesto: PresupuestoObra
  alCerrar: () => void
  alGuardarVersion: (motivo: string) => void
  alRestaurarVersion: (versionNum: number) => void
}

export function ModalVersionesPresupuesto({
  abierto,
  presupuesto,
  alCerrar,
  alGuardarVersion,
  alRestaurarVersion,
}: ModalVersionesPresupuestoProps) {
  const [nuevoMotivo, setNuevoMotivo] = useState('')
  const [mostrarCrear, setMostrarCrear] = useState(false)
  const [versionComparar1, setVersionComparar1] = useState<number | null>(null)
  const [versionComparar2, setVersionComparar2] = useState<number | null>(null)

  const formatearCOP = (val: number) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(val || 0)

  if (!abierto) return null

  const handleCrearSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!nuevoMotivo.trim()) return
    alGuardarVersion(nuevoMotivo)
    setNuevoMotivo('')
    setMostrarCrear(false)
  }

  const v1 = presupuesto.versiones?.find((v) => v.version === versionComparar1) || null
  const v2 = presupuesto.versiones?.find((v) => v.version === versionComparar2) || null

  return (
    <div className={styles.overlay} onClick={alCerrar}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <div className={styles.badgeTop}>CONTROL DE CAMBIOS Y TRAZABILIDAD</div>
            <h3 className={styles.titulo}>Versiones del Presupuesto {presupuesto.codigo}</h3>
            <p className={styles.subtitulo}>
              Histórico inmutable de versiones de cotización con fecha, motivo y comparador de variaciones.
            </p>
          </div>
          <button type="button" className={styles.btnCerrar} onClick={alCerrar}>
            ✕
          </button>
        </div>

        <div className={styles.accionesBar}>
          <button
            type="button"
            className={styles.btnNuevaVersion}
            onClick={() => setMostrarCrear(!mostrarCrear)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            <span>{mostrarCrear ? 'Ocultar Formulario' : 'Guardar Versión Actual'}</span>
          </button>
          <div className={styles.totalActual}>
            Total Actual en Borrador: <strong>{formatearCOP(presupuesto.total_presupuesto)}</strong>
          </div>
        </div>

        {mostrarCrear && (
          <form className={styles.formCrear} onSubmit={handleCrearSubmit}>
            <label className={styles.label}>Motivo o Justificación del Cambio:</label>
            <textarea
              required
              rows={2}
              placeholder="Ej: Modificación solicitada por interventoría en el metraje de cable 2/0 AWG..."
              value={nuevoMotivo}
              onChange={(e) => setNuevoMotivo(e.target.value)}
              className={styles.textarea}
              autoFocus
            />
            <div className={styles.formBotones}>
              <button type="button" className={styles.btnCancelar} onClick={() => setMostrarCrear(false)}>
                Cancelar
              </button>
              <button type="submit" className={styles.btnConfirmar}>
                Congelar Versión
              </button>
            </div>
          </form>
        )}

        {/* Lista de Versiones */}
        <div className={styles.cuerpo}>
          <div className={styles.seccionLista}>
            <h4 className={styles.seccionTitulo}>Historial de Versiones Registradas</h4>
            {(!presupuesto.versiones || presupuesto.versiones.length === 0) ? (
              <div className={styles.sinVersiones}>
                No hay versiones congeladas aún. Puede guardar una versión para registrar un hito contractual o compararla más adelante.
              </div>
            ) : (
              <div className={styles.versionesGrid}>
                {presupuesto.versiones.map((v) => {
                  const esV1 = versionComparar1 === v.version
                  const esV2 = versionComparar2 === v.version

                  return (
                    <div key={v.version} className={styles.versionCard}>
                      <div className={styles.versionTop}>
                        <span className={styles.numVersion}>Versión {v.version}</span>
                        <span className={styles.fechaVersion}>
                          {new Date(v.fecha).toLocaleString('es-CO')}
                        </span>
                      </div>
                      <div className={styles.motivoText}>&ldquo;{v.motivo}&rdquo;</div>
                      <div className={styles.totalVersion}>
                        Total: <strong>{formatearCOP(v.total)}</strong>
                      </div>
                      <div className={styles.versionFooter}>
                        <div className={styles.compararChecks}>
                          <label className={styles.checkLabel}>
                            <input
                              type="checkbox"
                              checked={esV1}
                              onChange={() => setVersionComparar1(esV1 ? null : v.version)}
                            />
                            Base A
                          </label>
                          <label className={styles.checkLabel}>
                            <input
                              type="checkbox"
                              checked={esV2}
                              onChange={() => setVersionComparar2(esV2 ? null : v.version)}
                            />
                            Base B
                          </label>
                        </div>
                        <button
                          type="button"
                          className={styles.btnRestaurar}
                          onClick={() => {
                            if (confirm(`¿Restaurar el presupuesto a la Versión ${v.version}? Los cambios no guardados se reemplazarán.`)) {
                              alRestaurarVersion(v.version)
                              alCerrar()
                            }
                          }}
                        >
                          Restaurar
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Comparador de 2 versiones */}
          {v1 && v2 && (
            <div className={styles.comparadorBox}>
              <h4 className={styles.seccionTitulo}>
                Comparación: Versión {v1.version} vs. Versión {v2.version}
              </h4>
              <div className={styles.comparadorTabla}>
                <div className={styles.compCol}>
                  <div className={styles.compHeader}>Versión {v1.version}</div>
                  <div className={styles.compRow}>Fecha: {new Date(v1.fecha).toLocaleDateString('es-CO')}</div>
                  <div className={styles.compRow}>Capítulos: {v1.capitulos?.length || 0}</div>
                  <div className={styles.compRow}>AIU: {v1.aiu?.porcentaje_admin || 0}% / {v1.aiu?.porcentaje_utilidad || 0}%</div>
                  <div className={styles.compTotal}>Total: {formatearCOP(v1.total)}</div>
                </div>
                <div className={styles.compDiferencia}>
                  <div className={styles.difLabel}>Variación Neta</div>
                  <div
                    className={styles.difValor}
                    style={{ color: v2.total >= v1.total ? '#059669' : '#dc2626' }}
                  >
                    {v2.total >= v1.total ? '+' : ''}
                    {formatearCOP(v2.total - v1.total)}
                  </div>
                  <div className={styles.difPct}>
                    {v1.total > 0
                      ? `${(((v2.total - v1.total) / v1.total) * 100).toFixed(1)}%`
                      : '0%'}
                  </div>
                </div>
                <div className={styles.compCol}>
                  <div className={styles.compHeader}>Versión {v2.version}</div>
                  <div className={styles.compRow}>Fecha: {new Date(v2.fecha).toLocaleDateString('es-CO')}</div>
                  <div className={styles.compRow}>Capítulos: {v2.capitulos?.length || 0}</div>
                  <div className={styles.compRow}>AIU: {v2.aiu?.porcentaje_admin || 0}% / {v2.aiu?.porcentaje_utilidad || 0}%</div>
                  <div className={styles.compTotal}>Total: {formatearCOP(v2.total)}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.btnCerrarModal} onClick={alCerrar}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

